import { SOLO_VIEWER_KEY } from '@components/solo-viewer/provider'
import { ReadonlyURLSearchParams } from 'next/navigation'
import { env } from 'environments'
import { Vertical } from './project'
import { mainnet } from 'viem/chains'

const getBaseUrl = () => {
  if (process.env.NEXT_PUBLIC_BASE_URL) {
    return process.env.NEXT_PUBLIC_BASE_URL
  }

  const isBrowser = typeof window !== 'undefined'
  const isUsingBranchUrl =
    isBrowser && window.location.hostname.includes(process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL ?? '')

  if (isUsingBranchUrl) {
    return `https://${process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL}`
  }

  return `https://${process.env.NEXT_PUBLIC_VERCEL_URL}`
}

export const BASE_URL = getBaseUrl()

export const homeRoute = (): string => {
  return '/'
}

export const artistsRoute = '/artists'

export const collectionsRoute = (queryString?: string): string => {
  return `/discover${queryString ?? ''}`
}

export const journalRoute = (queryString?: string): string => {
  return `/articles${queryString ?? ''}`
}

export const aboutRoute = '/articles/about-art-blocks'

export const projectShowRoute = (projectSlug: string): string => {
  return `/collection/${projectSlug}`
}

/**
 * Generates a route to display a specific token
 * @param contractAddress The contract address of the token
 * @param tokenId The on-chain token ID (not to be confused with the database token ID which is formatted as `${contractAddress}-${tokenId}`)
 * @returns The route string for the token show page
 */
export const tokenShowRoute = (contractAddress: string, tokenId: string | number): string => {
  return `/token/${contractAddress}/${tokenId}`
}

export const fullProjectPurchaseRoute = (projectSlug: string): string => {
  return `https://artblocks.io/curated${projectPurchaseRoute(projectSlug)}`
}

export const projectPurchaseRoute = (projectSlug: string): string => {
  if (!projectSlug) return allCollectionsRoute
  return `/collection/${projectSlug}/purchase`
}

export const artistRoute = (slug: string): string => {
  return `/artists/${slug}`
}

export const articleRoute = (slug: string): string => {
  return `/articles/${slug}`
}

export const ab500LearnMoreRoute = (): string => {
  return `${articleRoute('art-blocks-500-complete-collection')}`
}

export const ab500DiscoverRoute = (): string => {
  return `/discover/ab-500`
}

export const ab500TimelineRoute = (): string => {
  return `/timeline`
}

export const allExhibitionsRoute = (): string => {
  return `/exhibitions`
}

export const exhibitionShowRoute = (slug: string): string => {
  return `/exhibitions/${slug}`
}

export const fullExhibitionShowRoute = (slug: string): string => {
  return `${env.FLAGSHIP_URL}${exhibitionShowRoute(slug)}`
}

const MARKETPLACE_BASE_PATH = process.env.NEXT_PUBLIC_MARKETPLACE_BASE_PATH ?? '/marketplace'

// the below marketplace urls are just explicit so that they don't change per env,
// as we always want this to work in mainnet, but we are ok that sepolia doesn't
export const marketplaceProjectRoute = (projectId: string): string => {
  return `${MARKETPLACE_BASE_PATH}/collections/${projectId}`
}

export const marketplaceTokenRoute = (contractAddress: string, tokenId: string): string => {
  return `${MARKETPLACE_BASE_PATH}/asset/${contractAddress}/${tokenId}`
}

export const marketplaceTokenExportRoute = (contractAddress: string, tokenId: string): string => {
  return `${MARKETPLACE_BASE_PATH}/asset/${contractAddress}/${tokenId}/render`
}

export const marketplaceUserRoute = (walletAddress: string): string => {
  return `${MARKETPLACE_BASE_PATH}/wallets/${walletAddress}`
}

export const marketplaceUserOffersRoute = (walletAddress: string): string => {
  return `${marketplaceUserRoute(walletAddress)}?tab=offers`
}

export const marketplaceArtistRoute = (walletAddress: string): string => {
  return `${MARKETPLACE_BASE_PATH}/artists/${walletAddress}`
}

export const marketplaceArtistSlugRoute = (artistSlug: string): string => {
  return `${MARKETPLACE_BASE_PATH}/artists/${artistSlug}`
}

export const experimentsRoute = (): string => {
  return `${MARKETPLACE_BASE_PATH}/experiments`
}

export const gridExperimentRoute = (projectId: string): string => {
  return `${experimentsRoute()}/grids?collection=${projectId}`
}

export function soloViewerRoute(
  tokenId: string,
  currentPath: string,
  currentSearchParams: URLSearchParams | ReadonlyURLSearchParams
): string {
  const params = new URLSearchParams(currentSearchParams.toString())
  params.set(SOLO_VIEWER_KEY, tokenId)
  return `${currentPath}?${params.toString()}`
}

export const exploreRoute = (projectId: string): string => {
  return `${MARKETPLACE_BASE_PATH}/collections/${projectId}/explore`
}

export const allCollectionsRoute = '/discover'

export const verticalRoute = (vertical: Vertical): string => {
  return `/discover/${vertical}`
}

export const ETHERSCAN_BASE_URL = env.chain.blockExplorers?.default.url ?? mainnet.blockExplorers?.default.url

export const getEtherscanAddressURL = (contractAddress: string): string => {
  return `${ETHERSCAN_BASE_URL}/address/${contractAddress}`
}

export const getEtherscanTransactionURL = (transactionHash: string): string => {
  return `${ETHERSCAN_BASE_URL}/tx/${transactionHash}`
}

export const TOKEN_PAGE_REGEX = /\/token\/0x[a-fA-F0-9]{40}\/([0-9]+)$/

export const userProfileRoute = (walletOrUsername: string): string => {
  return `/profile/${walletOrUsername}`
}

export const userProfileSetsRoute = (walletOrUsername: string): string => {
  return `${userProfileRoute(walletOrUsername)}/sets`
}

export const userProfileSetRoute = (walletOrUsername: string, setSlug: string): string => {
  return `${userProfileSetsRoute(walletOrUsername)}/${setSlug}`
}

export function constructLiveViewUrlWithHash(projectId: string, tokenId: string, hash: string): string {
  return `${env.GENERATOR_BASE_URL}/${projectId}/${tokenId}/${hash}`
}
